/**
 * Lease entity returned by the back-office API.
 *
 * Mirrors LeaseResponse from app/domain/schemas/responses.py (camelCase).
 * See coloc-product-owner skill §13bis for the workflow.
 */


export type LeaseStatus = 'DRAFT' | 'PENDING_SIGNATURE' | 'SIGNED' | 'CANCELLED';


export interface Lease {
  id: string;
  candidateId: string;
  roomId: string;
  ownerId: string;

  status: LeaseStatus;

  leaseStartDate: string;
  leaseEndDate: string | null;
  monthlyRent: number;
  chargesProvision: number | null;
  depositAmount: number | null;
  agencyExpenses: number | null;

  targetSignatureDeadline: string | null;
  signedPdfUrl: string | null;
  signedAt: string | null;

  // EXTERNAL_PRE_SIGNED owner-declarative attestation
  externalSignatureAttestedAt: string | null;
  externalSignatureAttestedByUserId: string | null;
  externalSignatureNote: string | null;

  // Tenant-draft snapshot captured at acceptance, consumed on Lease.SIGNED
  tenantFirstName: string | null;
  tenantLastName: string | null;
  tenantEmail: string | null;
  tenantPhoneNumber: string | null;
  tenantGender: string | null;
  tenantDateOfBirth: string | null;
  tenantSchool: string | null;

  cancelledAt: string | null;
  cancellationReason: string | null;

  dateCreated: string;
  dateModified: string;
}

/**
 * Request payload for POST /api/leases/{id}/external-signed-document.
 * Sent as a JSON sidecar in the multipart upload (field name: `payload`).
 */
export interface LeaseExternalSignedFinalizeRequest {
  attestation_confirmed: boolean;
  signed_at?: string;
  external_signature_note?: string;
  /** Envoie l'email de refus aux candidats écartés par la signature. */
  notify_rejected_candidates?: boolean;
  // `password` retiré : le locataire définit lui-même son mot de passe via un
  // lien reçu par email (audit 2026-08-17, P1-4).
}

/** Request payload for POST /api/leases/{id}/cancel. */
export interface LeaseCancelRequest {
  reason?: string;
}

export const getLeaseStatusLabel = (status: LeaseStatus): string => {
  switch (status) {
    case 'DRAFT': return 'En attente de signature';
    // Ce statut ne concerne plus la seule signature électronique : c'est
    // désormais l'état de TOUTE réservation dont le bail n'est pas revenu
    // signé. Le libellé annonçait donc une signature en ligne qui n'a pas
    // lieu, sur un bail signé chez l'agence.
    case 'PENDING_SIGNATURE': return 'En attente du bail signé';
    case 'SIGNED': return 'Bail signé';
    case 'CANCELLED': return 'Annulé';
  }
};

